/**
 * 汎用カルーセルスライダーコンポーネント
 *
 * 使用例:
 * <fac-component-carousel-slider
 *   data-items-per-slide="3"
 *   data-gap="2"
 *   data-auto-play="true"
 *   data-auto-play-delay="5000"
 *   data-transition-duration="500"
 *   data-show-navigation="true"
 *   data-show-dots="false"
 *   data-show-index="false"
 *   data-slide-by-group="true"
 *   data-item-width="12.4rem">
 *   <ul class="carousel-track">
 *     <li class="carousel-item">...</li>
 *     <li class="carousel-item">...</li>
 *   </ul>
 * </fac-component-carousel-slider>
 */
if (!customElements.get('fac-component-carousel-slider')) {
  class FacComponentCarouselSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoPlayInterval = null;
      this.mediaQuery = window.matchMedia('(max-width: 768px)');
      this.isMobile = this.mediaQuery.matches;
    }

    connectedCallback() {
      // データ属性からパラメータを取得（デフォルト値を設定）
      // connectedCallback内で取得することで、動的に生成された要素でも確実に属性を読み取れる
      this.config = {
        itemsPerSlide: parseFloat(this.dataset.itemsPerSlide) || 3, // 小数値対応（例: 2.5）
        gap: parseFloat(this.dataset.gap) || 1.6, // rem単位
        autoPlay: this.dataset.autoPlay === 'true',
        autoPlayDelay: parseInt(this.dataset.autoPlayDelay) || 5000,
        transitionDuration: parseInt(this.dataset.transitionDuration) || 500,
        showNavigation: this.dataset.showNavigation !== 'false', // デフォルトtrue
        showDots: this.dataset.showDots === 'true', // デフォルトfalse
        showIndex: this.dataset.showIndex === 'true', // デフォルトfalse
        loop: this.dataset.loop === 'true', // デフォルトfalse
        slideByGroup: this.dataset.slideByGroup === 'true', // デフォルトfalse：表示数分まとめてスライド
        itemWidth: this.dataset.itemWidth || '12.4rem', // モバイル時のアイテム幅
      };

      // カルーセルトラックとアイテムを取得
      this.track = this.querySelector('.carousel-track');
      this.items = Array.from(this.querySelectorAll('.carousel-item'));

      if (!this.track || this.items.length === 0) {
        console.warn('FacComponentCarouselSlider: .carousel-track または .carousel-item が見つかりません');
        return;
      }

      // アイテム数がitemsPerSlide以下の場合はカルーセル不要だが、グリッドレイアウトは適用
      this.isCarouselMode = this.items.length > this.config.itemsPerSlide;

      // メディアクエリの変更を監視
      this.handleMediaChange = this.onMediaChange.bind(this);
      this.mediaQuery.addEventListener('change', this.handleMediaChange);

      this.init();
    }

    init() {
      this.setupCarousel();

      // カルーセルモードの場合のみナビゲーション、ドット、自動再生を有効化
      // モバイル(768px以下)の場合はカルーセル機能を無効化
      if (this.isCarouselMode && !this.isMobile) {
        if (this.config.showNavigation) {
          this.createNavigation();
        }

        if (this.config.showDots) {
          this.createDots();
        }

        if (this.config.autoPlay) {
          this.startAutoPlay();
        }
      }

      this.updateCarousel();

      // リサイズ時にカルーセルを更新
      this.resizeObserver = new ResizeObserver(() => {
        this.updateCarousel();
      });
      this.resizeObserver.observe(this);
    }

    onMediaChange(e) {
      this.isMobile = e.matches;

      // モバイル/デスクトップ切り替え時にカルーセルを再初期化
      if (this.isMobile) {
        // モバイル: カルーセル機能を無効化
        this.stopAutoPlay();
        this.removeNavigation();
        this.removeDots();
        this.currentIndex = 0;
      } else {
        // デスクトップ: カルーセル機能を有効化
        if (this.isCarouselMode) {
          if (this.config.showNavigation) {
            this.createNavigation();
          }
          if (this.config.showDots) {
            this.createDots();
          }
          if (this.config.autoPlay) {
            this.startAutoPlay();
          }
        }
      }

      this.setupCarousel();
      this.updateCarousel();
    }

    removeNavigation() {
      const navContainer = this.querySelector('.carousel-navigation');
      if (navContainer) {
        navContainer.remove();
        this.prevButton = null;
        this.nextButton = null;
        this.indexDisplay = null;
      }
    }

    removeDots() {
      const dotsContainer = this.querySelector('.carousel-dots');
      if (dotsContainer) {
        dotsContainer.remove();
        this.dots = null;
      }
    }

    get slideStep() {
      // slideByGroup有効時は表示数分（整数）まとめてスライド、無効時は1つずつ
      return this.config.slideByGroup ? Math.floor(this.config.itemsPerSlide) : 1;
    }

    get maxIndex() {
      // 最後のアイテムが見えるまでスクロールできる最大インデックス
      // itemsPerSlideが小数の場合（例: 2.5）は切り上げて整数に
      return Math.ceil(this.items.length - this.config.itemsPerSlide);
    }

    setupCarousel() {
      // モバイル(768px以下)の場合は横スクロール可能なレイアウト
      if (this.isMobile) {
        // 横スクロールレイアウト: 各アイテムに固定幅を設定
        this.track.style.gap = '1rem'; // モバイル用のgap
        this.track.style.transition = 'none';
        this.track.style.transform = 'none';
        // グリッドの代わりにauto-flowを使用して横並びに
        this.track.style.gridTemplateColumns = 'none';
        this.track.style.gridAutoFlow = 'column';
        this.track.style.gridAutoColumns = this.config.itemWidth; // 各アイテムの幅をパラメータから設定
        return;
      }

      // デスクトップ: 通常のgapを設定
      this.track.style.gap = `${this.config.gap}rem`;

      // デスクトップ: 通常のカルーセルレイアウト
      this.track.style.gridAutoFlow = 'initial';
      this.track.style.gridAutoColumns = 'initial';

      // グリッドの列数と各列の幅を設定
      const gapCount = this.config.itemsPerSlide - 1;
      const gapTotal = gapCount * this.config.gap;

      if (this.isCarouselMode) {
        // カルーセルモード: トランジションを有効化し、全アイテム分の列を作成
        this.track.style.transition = `transform ${this.config.transitionDuration}ms ease-in-out`;
        this.track.style.gridTemplateColumns = `repeat(${this.items.length}, calc((100% - ${gapTotal}rem) / ${this.config.itemsPerSlide}))`;
      } else {
        // 静的表示モード: itemsPerSlide分の列を作成（アイテムは実際の数だけ配置される）
        this.track.style.gridTemplateColumns = `repeat(${this.config.itemsPerSlide}, calc((100% - ${gapTotal}rem) / ${this.config.itemsPerSlide}))`;
      }
    }

    updateCarousel() {
      // モバイル(768px以下)の場合はカルーセル更新をスキップ
      if (this.isMobile) {
        return;
      }

      if (!this.isCarouselMode) {
        // 静的表示モードの場合は変形しない
        return;
      }

      // コンテナの幅を取得
      const containerWidth = this.offsetWidth;

      // gap の値を取得
      const gridStyle = getComputedStyle(this.track);
      const actualGap = parseFloat(gridStyle.gap) || parseFloat(gridStyle.columnGap) || 0;

      // 1アイテムの幅 = (コンテナ幅 - gap × (itemsPerSlide - 1)) / itemsPerSlide
      const itemWidth = (containerWidth - actualGap * (this.config.itemsPerSlide - 1)) / this.config.itemsPerSlide;

      // スライド距離 = アイテム幅 + gap
      const slideDistance = itemWidth + actualGap;

      // 移動量を計算
      const translateX = -this.currentIndex * slideDistance;

      this.track.style.transform = `translateX(${translateX}px)`;

      // アクセシビリティのため、表示されていないアイテムにaria-hiddenを設定
      this.items.forEach((item, index) => {
        const isVisible = index >= this.currentIndex && index < this.currentIndex + this.config.itemsPerSlide;
        item.setAttribute('aria-hidden', !isVisible);
      });

      this.updateNavigationState();

      if (this.config.showDots) {
        this.updateDots();
      }
    }

    createNavigation() {
      const navContainer = document.createElement('div');
      navContainer.className = 'carousel-navigation';

      const prevButton = document.createElement('button');
      prevButton.className = 'carousel-nav carousel-nav--prev';
      prevButton.setAttribute('aria-label', 'Previous slide');
      prevButton.innerHTML = `
    <svg width="5" height="9" viewBox="0 0 5 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.18141 -0.000233002L-0.000466003 4.18164L4.18141 8.36351L4.81739 7.72753L1.2715 4.18164L4.81739 0.635749L4.18141 -0.000233002Z" fill="currentColor"/>
    </svg>
    `;
      prevButton.addEventListener('click', () => this.prev());

      navContainer.appendChild(prevButton);

      // インデックス表示を有効にする場合
      if (this.config.showIndex) {
        const indexDisplay = document.createElement('div');
        indexDisplay.className = 'carousel-index';
        indexDisplay.setAttribute('aria-live', 'polite');
        indexDisplay.setAttribute('aria-atomic', 'true');
        navContainer.appendChild(indexDisplay);
        this.indexDisplay = indexDisplay;
      }

      const nextButton = document.createElement('button');
      nextButton.className = 'carousel-nav carousel-nav--next';
      nextButton.setAttribute('aria-label', 'Next slide');
      nextButton.innerHTML = `
      <svg width="5" height="9" viewBox="0 0 5 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0.635975 -0.000233002L4.81785 4.18164L0.635975 8.36351L-6.58276e-06 7.72753L3.54589 4.18164L-6.58276e-06 0.635749L0.635975 -0.000233002Z" fill="#333333"/>
      </svg>
    `;
      nextButton.addEventListener('click', () => this.next());

      navContainer.appendChild(nextButton);
      this.appendChild(navContainer);

      this.prevButton = prevButton;
      this.nextButton = nextButton;
    }

    createDots() {
      const dotsContainer = document.createElement('div');
      dotsContainer.className = 'carousel-dots';

      // ドット数 = 最大スライド数 + 1
      const dotCount = this.maxIndex + 1;

      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'carousel-dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => this.goToSlide(i));
        dotsContainer.appendChild(dot);
      }

      this.appendChild(dotsContainer);
      this.dots = Array.from(dotsContainer.querySelectorAll('.carousel-dot'));
    }

    updateNavigationState() {
      if (!this.config.showNavigation) return;

      if (this.config.loop) {
        // ループモードでは常にボタンを有効化
        this.prevButton.disabled = false;
        this.nextButton.disabled = false;
      } else {
        // 最初の位置では前ボタンを無効化
        this.prevButton.disabled = this.currentIndex === 0;
        // 最後の位置では次ボタンを無効化
        this.nextButton.disabled = this.currentIndex >= this.maxIndex;
      }

      // インデックス表示を更新
      this.updateIndex();
    }

    updateIndex() {
      if (!this.config.showIndex || !this.indexDisplay) return;

      const currentSlide = this.currentIndex + 1;
      const totalSlides = this.items.length;
      this.indexDisplay.textContent = `${currentSlide}/${totalSlides}`;
    }

    updateDots() {
      if (!this.config.showDots || !this.dots) return;

      this.dots.forEach((dot, index) => {
        if (index === this.currentIndex) {
          dot.classList.add('is-active');
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove('is-active');
          dot.removeAttribute('aria-current');
        }
      });
    }

    next() {
      const step = this.slideStep;
      if (this.config.loop && this.currentIndex >= this.maxIndex) {
        this.currentIndex = 0;
      } else if (this.currentIndex < this.maxIndex) {
        this.currentIndex = Math.min(this.currentIndex + step, this.maxIndex);
      }

      this.updateCarousel();
      this.resetAutoPlay();
    }

    prev() {
      const step = this.slideStep;
      if (this.config.loop && this.currentIndex === 0) {
        this.currentIndex = this.maxIndex;
      } else if (this.currentIndex > 0) {
        this.currentIndex = Math.max(this.currentIndex - step, 0);
      }

      this.updateCarousel();
      this.resetAutoPlay();
    }

    goToSlide(index) {
      if (index >= 0 && index <= this.maxIndex) {
        this.currentIndex = index;
        this.updateCarousel();
        this.resetAutoPlay();
      }
    }

    startAutoPlay() {
      if (!this.config.autoPlay) return;

      this.stopAutoPlay();
      this.autoPlayInterval = setInterval(() => {
        if (this.config.loop) {
          this.next();
        } else if (this.currentIndex >= this.maxIndex) {
          // ループしない場合は最後に到達したら停止
          this.stopAutoPlay();
        } else {
          this.next();
        }
      }, this.config.autoPlayDelay);
    }

    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    }

    resetAutoPlay() {
      if (!this.config.autoPlay) return;
      this.stopAutoPlay();
      this.startAutoPlay();
    }

    disconnectedCallback() {
      this.stopAutoPlay();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.handleMediaChange) {
        this.mediaQuery.removeEventListener('change', this.handleMediaChange);
      }
    }
  }

  customElements.define('fac-component-carousel-slider', FacComponentCarouselSlider);
}
